import { useAppSelector } from '@/hooks/store';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { FlexContainer } from '@/ui';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AddButton } from './AddButton';

const StyledFlexContainer = styled(FlexContainer)`
  border-radius: var(--semi-medium-border-radius);
  background: var(--gray-soft);
  padding: var(--semi-medium-padding);
  padding-left: 32px;
  margin-top: var(--default-padding);
  overflow-x: auto;
`;

const AddSimulationsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
`;

const AddSimulationsTitle = styled.h3`
  color: var(--black-main);
  font-size: var(--semi-big-font-size);
  font-weight: 700;
  margin: 0;
`;

const AddSimulationsDescription = styled.p`
  color: var(--black-main);
  font-size: var(--small-font-size);
  margin: 0;
  max-width: 350px;
  line-height: 1.4;
`;

const SimulationOptions = styled.div`
  display: flex;
  gap: var(--small-padding);
  min-width: 650px;
`;

type AddSimulationsCardProps = {
  disabledPlanWithAssistance?: boolean;
  onSingleSimulation?: () => void;
  onPlanWithAssistance?: () => void;
};

export const AddSimulationsCard: React.FC<AddSimulationsCardProps> = ({
  disabledPlanWithAssistance,
  onSingleSimulation,
  onPlanWithAssistance,
}) => {
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);

  return (
    <StyledFlexContainer justify="space-between">
      <AddSimulationsContent>
        <AddSimulationsTitle>
          {t('phishing.simulations.addCard.title')}
        </AddSimulationsTitle>
        <AddSimulationsDescription>
          {t('phishing.simulations.addCard.description')}
        </AddSimulationsDescription>
      </AddSimulationsContent>
      <SimulationOptions>
        <AddButton
          title={t('phishing.simulations.addCard.singleOption.title')}
          description={t(
            'phishing.simulations.addCard.singleOption.description',
          )}
          id="single-simulation"
          onClick={onSingleSimulation}
        />
        {/* TODO: enable for all users when the feature is stable */}
        {currentUser?.account?.isSystem && (
          <AddButton
            title={t('phishing.simulations.addCard.assistanceOption.title')}
            description={t(
              'phishing.simulations.addCard.assistanceOption.description',
            )}
            id="plan-with-assistance"
            disabled={disabledPlanWithAssistance}
            onClick={onPlanWithAssistance}
          />
        )}
        {!!disabledPlanWithAssistance && (
          <Tooltip
            target="#plan-with-assistance"
            content={t(
              'phishing.simulations.addCard.assistanceOption.disabled.tooltip',
            )}
            position="top"
            showOnDisabled
          />
        )}
      </SimulationOptions>
    </StyledFlexContainer>
  );
};
